import { css, html, LitElement, nothing, TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { SharePermission, ShareRecord } from "../shared/api.js";
import { authSession, getAuthUser, isAuthConfigured, loginInstructor, whenAuthReady, type AuthUser } from "./auth0.js";
import { SuccessEvent } from "./event.success.js";
import { WarningEvent } from "./event.warning.js";
import { backIcon } from "./icons.js";
import { navigate, shareProfileIdFromPath } from "./nav.js";
import { readingBeeApi } from "./sql-api.js";
import { shareOrCopy } from "../shared/share.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { ReadingBeeModal } from "./component.modal.js";
import { dispatch } from "./util.events.js";
import "./component.instructor-gate.js";

@customElement("reading-bee-share-profile")
export class ReadingBeeShareProfile extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        min-height: 100%;
      }

      .page {
        min-height: 100dvh;
      }

      header {
        position: sticky;
        top: 0;
        z-index: 2;
        display: grid;
        grid-template-columns: 44px 1fr 44px;
        align-items: center;
        padding: calc(0.7rem + var(--inset-top)) 1rem 0.8rem;
        background: linear-gradient(to bottom, #0c0b09 70%, rgba(12, 11, 9, 0.86));
        border-bottom: 1px solid var(--color-panel-border);
      }

      header h1 {
        margin: 0;
        font-size: 1.2rem;
        text-align: center;
      }

      .back {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--color-primary-text);
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .body {
        width: min(560px, 100%);
        margin: 0 auto;
        padding: 1.7rem 1.2rem calc(2rem + env(safe-area-inset-bottom));
      }

      .body h2 {
        margin: 0 0 0.6rem;
        font-size: 1.25rem;
      }

      .body p {
        margin: 0 0 1.1rem;
      }

      label {
        display: block;
        margin: 0 0 1.1rem;
        font-size: 0.92rem;
        color: var(--color-primary-text-muted);
      }

      label input {
        display: block;
        margin-top: 0.45rem;
      }

      .combo {
        position: relative;
        margin-top: 0.45rem;
      }

      .combo input {
        margin-top: 0;
      }

      .combo.has-known input {
        padding-right: 2.8rem;
      }

      .combo-toggle {
        position: absolute;
        top: 50%;
        right: 0.35rem;
        width: 40px;
        height: 40px;
        transform: translateY(-50%);
        border-radius: 10px;
        color: var(--color-primary-text-muted);
        display: grid;
        place-items: center;
      }

      .combo-toggle:hover,
      .combo-toggle[aria-expanded="true"] {
        color: var(--color-primary-text);
        background: #2c261f;
      }

      .combo-caret {
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 6px solid currentColor;
      }

      .combo-list {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(100% + 6px);
        z-index: 4;
        margin: 0;
        padding: 0.35rem;
        list-style: none;
        max-height: 220px;
        overflow: auto;
        background: #221e18;
        border: 1px solid var(--color-panel-border);
        border-radius: 14px;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.28);
      }

      .combo-option {
        display: block;
        width: 100%;
        text-align: left;
        padding: 0.7rem 0.8rem;
        border-radius: 10px;
        color: var(--color-primary-text);
      }

      .combo-option:hover,
      .combo-option.active {
        background: #2c261f;
      }

      .primary-btn {
        width: 100%;
        margin: 0;
      }

      .primary-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
      }

      .shares {
        margin: 1.6rem 0 0;
        display: grid;
        gap: 0.7rem;
      }

      .shares h3 {
        margin: 0;
        font-size: 1rem;
      }

      .share-row {
        display: grid;
        gap: 0.85rem;
        padding: 1rem;
        border: 1px solid var(--color-panel-border);
        border-radius: 8px;
        background: var(--color-panel);
      }

      .share-recipient {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .share-recipient strong {
        flex: 1 1 12rem;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .share-permission {
        flex: 0 0 auto;
        color: var(--color-muted-text);
        font-size: 0.9rem;
      }

      .share-status {
        flex: 0 0 auto;
        padding: 0.25rem 0.55rem;
        border-radius: 999px;
        background: var(--color-panel-border);
        color: var(--color-primary-text);
        font-size: 0.78rem;
        text-transform: capitalize;
      }

      .share-status.status-accepted {
        border: 1px solid rgba(108, 190, 126, 0.45);
        color: #9fe0aa;
      }

      .share-status.status-pending {
        border: 1px solid rgba(229, 186, 91, 0.5);
        color: #f0ce78;
      }

      .share-status.status-declined,
      .share-status.status-expired,
      .share-status.status-revoked {
        border: 1px solid rgba(216, 116, 116, 0.5);
        color: #eeaaaa;
      }

      .share-actions {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        overflow: hidden;
        border-radius: 999px;
      }

      .share-actions button {
        min-width: 0;
        min-height: 48px;
        padding: 0.55rem 0.65rem;
        background: rgba(244, 234, 213, 0.11);
        color: var(--color-primary-text);
        border: 0;
        border-radius: 0;
        font-size: 1rem;
        transition:
          background var(--time-fast) ease,
          color var(--time-fast) ease;
      }

      .share-actions button:hover:not(:disabled) {
        background: rgba(244, 234, 213, 0.22);
      }

      .share-actions button + button {
        border-left: 1px solid var(--color-panel-border);
      }

      .share-actions .stop-share-btn {
        background: rgba(244, 234, 213, 0.11);
        color: var(--color-primary-text);
      }

      .share-actions .stop-share-btn:hover:not(:disabled) {
        background: rgba(216, 116, 116, 0.24);
        color: #eeaaaa;
      }

      .permission-form {
        display: grid;
        gap: 0.75rem;
      }

      .permission-form select {
        width: 100%;
      }

      .permission-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0;
      }

      .permission-actions button {
        width: auto;
        min-width: 7rem;
        min-height: 44px;
        border-radius: 0;
      }

      .permission-actions button:first-child {
        border-radius: 999px 0 0 999px;
      }

      .permission-actions button:last-child {
        border-radius: 0 999px 999px 0;
      }

      .permission-actions button + button {
        border-left: 1px solid var(--color-panel-border);
      }
    `,
  ];

  @state() private email = "";
  @state() private busy = false;
  @state() private shares: ShareRecord[] = [];
  @state() private knownEmails: string[] = [];
  @state() private permission: SharePermission = "contribute";
  @state() private comboOpen = false;
  @state() private activeIndex = 0;
  @state() private revoking: string | null = null;
  @state() private authReady = false;
  @state() private authUser: AuthUser | null = getAuthUser();
  @query(".share-link-modal") private shareLinkModal!: ReadingBeeModal;
  @query(".permission-modal") private permissionModal!: ReadingBeeModal;
  @query(".stop-sharing-modal") private stopSharingModal!: ReadingBeeModal;
  @state() private permissionShare: ShareRecord | null = null;
  @state() private editPermission: SharePermission = "contribute";
  @state() private stopSharingShare: ShareRecord | null = null;

  private blurTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    authSession.addEventListener("change", this.onAuthChange);
    void this.bootAuth();
  }

  override disconnectedCallback(): void {
    this.clearBlurTimer();
    authSession.removeEventListener("change", this.onAuthChange);
    super.disconnectedCallback();
  }

  private onAuthChange = (): void => {
    this.authUser = getAuthUser();
    if (this.authUser) void this.refreshShares();
  };

  private bootAuth = async (): Promise<void> => {
    await whenAuthReady();
    this.authUser = getAuthUser();
    this.authReady = true;
    void this.refreshShares();
  };

  override render(): TemplateResult {
    const profileId = shareProfileIdFromPath();
    const profile = profileId ? appStore.state.profiles.find((item) => item.id === profileId) : null;
    return html`
      <div class="page">
        <header>
          <button class="back" aria-label="Back" @click=${() => navigate("settings")}>${backIcon}</button>
          <h1>Share profile</h1>
        </header>
        <div class="body">
          <reading-bee-instructor-gate>${this.body(profile?.name ?? "this profile")}</reading-bee-instructor-gate>
        </div>
      </div>
      <reading-bee-modal class="share-link-modal" @ModelClosing=${this.closeShareLinkModal}>
        <h2 slot="title">Share link copied</h2>
        <div slot="body">
          <p>
            The share link has been copied to your clipboard. Send it to whoever you want to share this profile with.
          </p>
          <button class="primary-btn" @click=${() => this.shareLinkModal.close()}>Done</button>
        </div>
      </reading-bee-modal>
      <reading-bee-modal class="permission-modal" @ModelClosing=${this.closePermissionModal}>
        <h2 slot="title">Change access</h2>
        <div slot="body" class="permission-form">
          <p>Choose what this account can do with the shared profile.</p>
          <label>
            Access
            <select .value=${this.editPermission} @change=${this.onEditPermission}>
              <option value="contribute">Can contribute</option>
              <option value="view">View only</option>
            </select>
          </label>
          <div class="permission-actions">
            <button class="ghost-btn" @click=${() => this.permissionModal.close()}>Cancel</button>
            <button class="primary-btn" ?disabled=${this.busy} @click=${this.savePermission}>Save</button>
          </div>
        </div>
      </reading-bee-modal>
      <reading-bee-modal class="stop-sharing-modal" @ModelClosing=${this.closeStopSharingModal}>
        <h2 slot="title">Stop sharing?</h2>
        <div slot="body" class="permission-form">
          <p>They will no longer be able to access this student profile.</p>
          <div class="confirm-row">
            <button class="ghost-btn" @click=${() => this.stopSharingModal.close()}>Cancel</button>
            <button class="danger-btn" ?disabled=${this.busy} @click=${this.confirmStopSharing}>Stop sharing</button>
          </div>
        </div>
      </reading-bee-modal>
    `;
  }

  private body(name: string): TemplateResult {
    if (!this.authReady) {
      return html`
        <p>Loading your account…</p>
      `;
    }
    if (!this.authUser) {
      return html`
        <p>Sign in as the instructor to share ${name} with another teacher by email.</p>
        <button
          class="primary-btn"
          ?disabled=${!isAuthConfigured()}
          @click=${() => loginInstructor(`${window.location.pathname}${window.location.search}`)}>
          Sign in
        </button>
      `;
    }
    if (appStore.isIncomingShare(shareProfileIdFromPath() ?? "")) {
      return html`
        <p>You can use ${name}, but only the owner can share this profile with someone else.</p>
      `;
    }
    return html`
      <h2>Sharing profile: ${name}</h2>
      <p>
        Enter the email of the person who should get access to this profile. You can choose whether they may contribute
        learning facts.
      </p>
      <label>Teacher email ${this.emailField()}</label>
      <label>
        Permission
        <select .value=${this.permission} @change=${this.onPermission}>
          <option value="contribute">Can contribute</option>
          <option value="view">View only</option>
        </select>
      </label>
      <button class="primary-btn" ?disabled=${this.busy || !this.email.trim()} @click=${this.share}>Share</button>
      <div class="shares">
        <h3>Currently shared with</h3>
        ${
          this.shares.length === 0
            ? html`
                <p>This profile is not shared with anyone yet.</p>
              `
            : this.shares.map(
                (share) => html`
                  <div class="share-row">
                    <div class="share-recipient">
                      <strong>${share.email ?? share.ownerDisplayName ?? "Shared account"}</strong>
                      <span class="share-permission">${this.permissionLabel(share.permission)}</span>
                      <span class="share-status status-${share.status}">${this.statusLabel(share.status)}</span>
                    </div>
                    <div class="share-actions">
                      <button class="ghost-btn" ?disabled=${this.busy} @click=${() => this.copyShareLink(share)}>
                        Copy link
                      </button>
                      <button class="ghost-btn" ?disabled=${this.busy} @click=${() => this.openPermission(share)}>
                        Change access
                      </button>
                      <button
                        class="stop-share-btn"
                        ?disabled=${this.revoking === share.shareId}
                        @click=${() => this.openStopSharing(share)}>
                        Stop sharing
                      </button>
                    </div>
                  </div>
                `,
              )
        }
      </div>
    `;
  }

  private emailField(): TemplateResult {
    const suggestions = this.filteredEmails();
    const showList = this.comboOpen && this.knownEmails.length > 0 && suggestions.length > 0;
    const active = suggestions[this.activeIndex] ?? "";
    return html`
      <div class="combo ${this.knownEmails.length > 0 ? "has-known" : ""}">
        <input
          type="email"
          autocomplete="off"
          placeholder="teacher@school.edu"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded=${showList ? "true" : "false"}
          aria-controls="email-options"
          aria-activedescendant=${showList && active ? `email-option-${this.activeIndex}` : nothing}
          .value=${this.email}
          @input=${this.onEmail}
          @focus=${this.openCombo}
          @blur=${this.scheduleCloseCombo}
          @keydown=${this.onEmailKey} />
        ${
          this.knownEmails.length > 0
            ? html`
                <button
                  class="combo-toggle"
                  type="button"
                  aria-label="Show previous emails"
                  aria-expanded=${showList ? "true" : "false"}
                  @mousedown=${this.keepCombo}
                  @click=${this.toggleCombo}>
                  <span class="combo-caret"></span>
                </button>
              `
            : nothing
        }
        ${
          showList
            ? html`
                <ul class="combo-list" id="email-options" role="listbox">
                  ${suggestions.map(
                    (item, index) => html`
                      <li role="presentation">
                        <button
                          id=${`email-option-${index}`}
                          class="combo-option ${index === this.activeIndex ? "active" : ""}"
                          type="button"
                          role="option"
                          aria-selected=${index === this.activeIndex ? "true" : "false"}
                          @mousedown=${this.keepCombo}
                          @click=${() => this.pickEmail(item)}>
                          ${item}
                        </button>
                      </li>
                    `,
                  )}
                </ul>
              `
            : nothing
        }
      </div>
    `;
  }

  private filteredEmails(): string[] {
    const needle = this.email.trim().toLowerCase();
    return needle ? this.knownEmails.filter((email) => email.includes(needle)) : this.knownEmails;
  }

  private permissionLabel(permission: SharePermission): string {
    return permission === "contribute" ? "Can contribute" : "View only";
  }

  private statusLabel(status: ShareRecord["status"]): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  private onPermission = (event: Event): void => {
    this.permission = (event.target as HTMLSelectElement).value as SharePermission;
  };

  private onEmail = (event: Event): void => {
    this.email = (event.target as HTMLInputElement).value;
    this.activeIndex = 0;
    if (this.knownEmails.length > 0) this.comboOpen = true;
  };

  private openCombo = (): void => {
    this.clearBlurTimer();
    if (this.knownEmails.length > 0) this.comboOpen = true;
  };

  private toggleCombo = (event: Event): void => {
    event.preventDefault();
    this.clearBlurTimer();
    this.comboOpen = this.knownEmails.length > 0 && !this.comboOpen;
  };

  private keepCombo = (event: Event): void => {
    event.preventDefault();
    this.clearBlurTimer();
  };

  private scheduleCloseCombo = (): void => {
    this.clearBlurTimer();
    this.blurTimer = window.setTimeout(() => {
      this.comboOpen = false;
    }, 120);
  };

  private clearBlurTimer(): void {
    if (this.blurTimer != null) {
      window.clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
  }

  private pickEmail = (email: string): void => {
    this.email = email;
    this.comboOpen = false;
    this.activeIndex = 0;
  };

  private onEmailKey = (event: KeyboardEvent): void => {
    const suggestions = this.filteredEmails();
    if (event.key === "Escape") {
      this.comboOpen = false;
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (this.knownEmails.length === 0) return;
      this.comboOpen = true;
      this.activeIndex = suggestions.length === 0 ? 0 : (this.activeIndex + 1) % suggestions.length;
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!this.comboOpen || suggestions.length === 0) return;
      this.activeIndex = (this.activeIndex - 1 + suggestions.length) % suggestions.length;
      return;
    }
    if (event.key === "Enter") {
      const chosen = suggestions[this.activeIndex];
      if (this.comboOpen && chosen) {
        event.preventDefault();
        this.pickEmail(chosen);
      }
    }
  };

  private refreshShares = async (): Promise<void> => {
    const profileId = shareProfileIdFromPath();
    if (!profileId || !getAuthUser() || appStore.isIncomingShare(profileId)) return;
    try {
      const response = await readingBeeApi.listShares();
      this.shares = response.shares.filter((share) => share.studentId === profileId && share.status !== "revoked");
      this.knownEmails = this.shares.map((share) => share.email).filter((email): email is string => Boolean(email));
    } catch (error) {
      dispatch(
        this,
        WarningEvent(error instanceof Error ? error.message : "Could not load who this profile is shared with"),
      );
    }
  };

  private share = async (): Promise<void> => {
    const profileId = shareProfileIdFromPath();
    const profile = profileId ? appStore.state.profiles.find((item) => item.id === profileId) : null;
    if (!profileId || !profile) return;
    this.busy = true;
    try {
      const created = await readingBeeApi.createShare(profileId, { email: this.email, permission: this.permission });
      this.email = "";
      this.comboOpen = false;
      const result = await shareOrCopy(created.url, "Reading Bee", `${profile.name}'s Reading Bee profile`);
      if (result === "copied") {
        dispatch(this, SuccessEvent("Share link copied"));
      }
      await this.refreshShares();
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not share this profile"));
    } finally {
      this.busy = false;
    }
  };

  private copyShareLink = async (share: ShareRecord): Promise<void> => {
    const profileId = shareProfileIdFromPath();
    if (!profileId || !share.email) return;
    this.busy = true;
    try {
      const profile = appStore.state.profiles.find((item) => item.id === profileId);
      const created = await readingBeeApi.createShare(profileId, {
        email: share.email,
        permission: share.permission,
      });
      await navigator.clipboard.writeText(created.url);
      await this.refreshShares();
      await this.shareLinkModal.open();
      if (profile) dispatch(this, SuccessEvent(`Share link copied for ${profile.name}`));
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not copy the share link"));
    } finally {
      this.busy = false;
    }
  };

  private closeShareLinkModal = (): void => {};

  private openPermission = (share: ShareRecord): void => {
    this.permissionShare = share;
    this.editPermission = share.permission;
    void this.permissionModal.open();
  };

  private closePermissionModal = (): void => {
    this.permissionShare = null;
  };

  private openStopSharing = (share: ShareRecord): void => {
    this.stopSharingShare = share;
    void this.stopSharingModal.open();
  };

  private closeStopSharingModal = (): void => {
    this.stopSharingShare = null;
  };

  private confirmStopSharing = async (): Promise<void> => {
    const share = this.stopSharingShare;
    if (!share) return;
    await this.stopSharingModal.close();
    await this.stopSharing(share);
  };

  private onEditPermission = (event: Event): void => {
    this.editPermission = (event.target as HTMLSelectElement).value as SharePermission;
  };

  private savePermission = async (): Promise<void> => {
    const profileId = shareProfileIdFromPath();
    const share = this.permissionShare;
    if (!profileId || !share) return;
    this.busy = true;
    try {
      await readingBeeApi.updateSharePermission(profileId, share.shareId, this.editPermission);
      await this.refreshShares();
      await this.permissionModal.close();
      dispatch(this, SuccessEvent("Access updated"));
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not update access"));
    } finally {
      this.busy = false;
    }
  };

  private stopSharing = async (share: ShareRecord): Promise<void> => {
    const profileId = shareProfileIdFromPath();
    if (!profileId) return;
    this.revoking = share.shareId;
    try {
      await readingBeeApi.revokeShare(profileId, share.shareId);
      await this.refreshShares();
      dispatch(this, SuccessEvent(`Stopped sharing with ${share.email ?? "this account"}`));
    } catch (error) {
      dispatch(this, WarningEvent(error instanceof Error ? error.message : "Could not stop sharing"));
    } finally {
      this.revoking = null;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-share-profile": ReadingBeeShareProfile;
  }
}
